import { Box, Button } from '@mui/material';
import useDisputes from 'api/useDisputes';
import useOrders from 'api/useOrders';
import {
  IDisputeEvidence,
  DisputeEvidenceType,
  DisputeStatus
} from 'common/contracts';
import AcFileInput from 'components/AcFileInput/AcFileInput';
import AcInput from 'components/AcInput/AcInput';
import AcSelect from 'components/AcSelect/AcSelect';
import { ELocalStorageKeys } from 'constants/enums';
import { useFormik } from 'formik';
import { UserDetails } from 'pages/Login/Login.types';
import { useLocation, useParams } from 'react-router-dom';
import { localStorageUtil } from 'utils/localStorageUtil';
import * as yup from 'yup';
import { EToasterStatus, showToast } from '../../../utils/showToast';
import { useEffect } from 'react';
import ActionButton from 'components/ActionButton/ActionButton';

export default function AddNewEvidence({ orderParams }: { orderParams: any }) {
  const { orderId } = useParams();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const publisherId = searchParams.get('publisherId');

  const { getOrderDetails: order } = useOrders(orderId, undefined, undefined, {
    selectedProjects: orderParams.publisherId
  });

  const { addDisputeEvidence } = useDisputes(
    order.data?.dispute?._id,
    publisherId ?? ''
  );

  const userDetails = localStorageUtil.get<UserDetails>(
    ELocalStorageKeys.USER_DETAILS
  );

  const disputeEvidenceFormSchema = yup.object().shape({
    type: yup.string().required('type is required'),
    name: yup.string().required('name is required'),
    description: yup.string().required('description is required'),
    link: yup.string().optional(),
    file: yup.mixed().optional()
  });

  const {
    values,
    handleBlur,
    handleChange,
    isValid,
    submitForm,
    setFieldValue,
    errors,
    resetForm
  } = useFormik<Partial<IDisputeEvidence>>({
    validationSchema: disputeEvidenceFormSchema,
    validateOnMount: true,
    initialValues: {
      name: '',
      description: '',
      type: undefined,
      file: undefined
    },
    onSubmit: (values, helpers) => {
      const newEvidence: Partial<IDisputeEvidence> = {
        name: values.name,
        description: values.description,
        username: `${userDetails.firstName} ${userDetails.lastName}`,
        type: values.type,
        file: values.file
      };
      addDisputeEvidence.mutate(newEvidence, {
        onSuccess: () => {
          order.refetch();
          helpers.resetForm();
          helpers.validateForm();
          resetForm();
        },
        onError: () => {
          showToast({
            message: 'Failed to add evidence',
            status: EToasterStatus.ERROR
          });
        }
      });
    }
  });

  const isEvidenceSubmissionDisabled =
    order.data?.dispute.status === DisputeStatus.EVIDENCE_SUBMITTED ||
    order.data?.dispute.status === DisputeStatus.WON ||
    order.data?.dispute.status === DisputeStatus.LOST ||
    order.data?.dispute.status === DisputeStatus.UNDER_REVIEW;

  useEffect(() => {
    if (isEvidenceSubmissionDisabled) {
      resetForm();
      handleFileSelect(null);
    }
  }, [isEvidenceSubmissionDisabled, resetForm]);

  const handleFileSelect = (file: File | null) => {
    setFieldValue('file', file);
  };

  return (
    <Box pt={4} maxWidth={'520px'}>
      <Box display="grid" gridTemplateColumns="repeat(12, 1fr)" gap={2}>
        <Box gridColumn="span 6">
          <AcInput
            header="Name"
            name="name"
            value={values.name}
            onChange={handleChange}
            onBlur={handleBlur}
            required
            disabled={isEvidenceSubmissionDisabled}
          />
        </Box>
        <Box gridColumn="span 6">
          <AcSelect
            items={Object.values(DisputeEvidenceType).map((type) => ({
              label: type,
              value: type,
              content: type
            }))}
            header="Type"
            name="type"
            value={values.type || ''}
            onChange={handleChange}
            onBlur={handleBlur}
            required
            disabled={isEvidenceSubmissionDisabled}
          ></AcSelect>
        </Box>
        <Box gridColumn="span 6">
          <AcFileInput
            header="Upload File"
            onFileSelect={handleFileSelect}
            value={values.file}
            disabled={isEvidenceSubmissionDisabled}
          />
        </Box>
        <Box gridColumn="span 12">
          <AcInput
            header="Description"
            name="description"
            textarea
            value={values.description}
            onChange={handleChange}
            onBlur={handleBlur}
            required
            disabled={isEvidenceSubmissionDisabled}
          />
        </Box>
        <Box gridColumn="span 4">
          <ActionButton
            disabled={
              !isValid ||
              addDisputeEvidence.isLoading ||
              isEvidenceSubmissionDisabled
            }
            variant="outlined"
            onClick={submitForm}
            sx={{ width: '100%' }}
            text={addDisputeEvidence.isLoading ? 'Adding...' : 'Add Evidence'}
            isLoading={addDisputeEvidence.isLoading}
          />
        </Box>
      </Box>
    </Box>
  );
}
